import { all, select, takeEvery, takeLatest, fork, call, put } from 'redux-saga/effects'
import { PayloadAction } from '@reduxjs/toolkit'

// action
import { settlementActions, configActions } from '@redux/store/index'

// api
import { settlementApi, orderApi, ResType } from '@api'

// lib
import { Logger } from '@utils'

// type
import {
	SettlementRow,
	SettlementMonthSummary,
	SettlementPatchType,
	SettlementBulkType,
} from '@schemas'
import { SettlementStateType } from '@redux/types'
import { weekMonths, rowInWeek } from '@components/systems/settlement/settlement.format'

const isOk = (res: ResType | undefined): boolean =>
	res !== undefined && res.status >= 200 && res.status < 300

const errorText = (res: ResType | undefined, fallback: string): string => {
	const data: any = res?.data
	const msg = data?.message ?? data
	const title = msg?.meta?.title
	if (Array.isArray(title)) return String(title[0] ?? fallback)
	if (typeof title === 'string' && title !== '') {
		if (title.includes('LOCKED'))
			return '납부 체크된 주문은 값을 바꿀 수 없습니다. 먼저 체크를 풀어 주세요.'
		return title
	}
	if (typeof msg?.errorMessage === 'string') return msg.errorMessage
	return fallback
}

/** 상단 — 한 해의 월 × 통화 집계 (`GET /settlement/summary?year=`) */
function* fetchSummary() {
	try {
		const year: string = yield select(state => state.settlement.year)
		yield put(settlementActions.SUMMARY_STATUS('loading'))
		const res: ResType | undefined = yield call(settlementApi.getSettlementSummary, {
			query: { year },
		})
		if (res !== undefined && isOk(res)) {
			const list = (res.data as any[]).map(a => new SettlementMonthSummary(a))
			yield put(settlementActions.SUMMARY(list))
			yield put(settlementActions.SUMMARY_STATUS(list.length > 0 ? 'ok' : 'empty'))
		} else {
			yield put(settlementActions.SUMMARY([]))
			yield put(settlementActions.SUMMARY_STATUS('empty'))
		}
	} catch (error) {
		Logger.warn(error)
		yield put(settlementActions.SUMMARY_STATUS('empty'))
	}
}

/**
 * 판매 행 — 탭이 정한 기간. 월간 = 그 달(`GET /settlement?month=`) · 주간 = 걸친 달(하나 또는 둘)을 받아 주문일로 자른다.
 * 연간 탭은 행이 없다. 통화 필터는 쓰지 않는다 — 전부 원화로 환산해 보인다(2026-09-27).
 */
function* fetchRows() {
	try {
		const unit: SettlementStateType['unit'] = yield select(state => state.settlement.unit)
		if (unit === 'year') return
		const month: string = yield select(state => state.settlement.month)
		const week: string = yield select(state => state.settlement.week)
		yield put(settlementActions.ROWS_STATUS('loading'))
		const months: string[] = unit === 'week' ? weekMonths(week) : [month]
		let list: SettlementRow[] = []
		for (const m of months) {
			const res: ResType | undefined = yield call(settlementApi.getSettlementRows, {
				query: { month: m, currency: '' },
			})
			if (res !== undefined && isOk(res)) {
				list = list.concat((res.data as any[]).map(a => new SettlementRow(a)))
			}
		}
		if (unit === 'week') list = list.filter(r => rowInWeek(r, week))
		yield put(settlementActions.ROWS(list))
		yield put(settlementActions.ROWS_STATUS(list.length > 0 ? 'ok' : 'empty'))
	} catch (error) {
		Logger.warn(error)
		yield put(settlementActions.ROWS_STATUS('empty'))
	}
}

function* init() {
	yield all([call(fetchSummary), call(fetchRows)])
}

/** 체크 · 메모 · 비용 한 건 — 즉시 저장. 응답 한 행으로 바꿔 넣고 상단 집계를 다시 읽는다 */
function* updateOne(
	action: PayloadAction<{ orderId: string; body: SettlementPatchType } | null>,
) {
	if (action.payload === null) return
	const { orderId, body } = action.payload
	try {
		const saving: string[] = yield select(state => state.settlement.savingOrderIds)
		yield put(settlementActions.SAVING_ORDER_IDS([...saving, orderId]))
		const res: ResType | undefined = yield call(settlementApi.updateSettlement, {
			params: { orderId },
			body,
		})
		if (res !== undefined && isOk(res)) {
			const row = new SettlementRow((res.data as any[])[0])
			yield put(settlementActions.ROW(row))
			yield call(fetchSummary)
		} else {
			yield put(
				configActions.TOAST({
					isShow: true,
					message: errorText(res, '정산 저장에 실패했습니다.'),
				}),
			)
			// 화면이 먼저 그린 값을 되돌린다
			yield call(fetchRows)
		}
	} catch (error) {
		Logger.warn(error)
	} finally {
		const saving: string[] = yield select(state => state.settlement.savingOrderIds)
		yield put(settlementActions.SAVING_ORDER_IDS(saving.filter(id => id !== orderId)))
	}
}

/** 다중 선택 → 부가세 납부 처리 / 후원금 납부 처리 */
function* bulk(action: PayloadAction<SettlementBulkType | null>) {
	if (action.payload === null) return
	try {
		yield put(settlementActions.BULK_SAVING(true))
		const res: ResType | undefined = yield call(settlementApi.bulkUpdateSettlement, {
			body: action.payload,
		})
		if (isOk(res)) {
			yield put(
				configActions.TOAST({
					isShow: true,
					message: `${action.payload.orderIds.length}건을 처리했습니다.`,
				}),
			)
			yield put(settlementActions.SELECTED_ORDER_IDS([]))
		} else {
			yield put(
				configActions.TOAST({
					isShow: true,
					message: errorText(res, '일괄 처리에 실패했습니다.'),
				}),
			)
		}
		yield all([call(fetchRows), call(fetchSummary)])
	} catch (error) {
		Logger.warn(error)
	} finally {
		yield put(settlementActions.BULK_SAVING(false))
	}
}

/** 입금 대기(도매) → 입금 확인. 한 건이 실패해도 끝까지 돌린다. 서버 훅이 정산 행을 만든다 */
function* markPaid(action: PayloadAction<string[] | null>) {
	if (action.payload === null || action.payload.length === 0) return
	try {
		yield put(settlementActions.BULK_SAVING(true))
		let okCount = 0
		let failText = ''
		for (const orderId of action.payload) {
			const res: ResType | undefined = yield call(orderApi.markOrderPaid, {
				params: { orderId },
				body: { amount: null, ref: '' },
			})
			if (isOk(res)) okCount += 1
			else if (failText === '') failText = errorText(res, '입금 확인에 실패했습니다.')
		}
		yield put(
			configActions.TOAST({
				isShow: true,
				message:
					failText === ''
						? `${okCount}건 입금 확인.`
						: `${okCount}건 입금 확인, ${
								action.payload.length - okCount
						  }건 실패 — ${failText}`,
			}),
		)
		yield put(settlementActions.SELECTED_ORDER_IDS([]))
		yield all([call(fetchRows), call(fetchSummary)])
	} catch (error) {
		Logger.warn(error)
	} finally {
		yield put(settlementActions.BULK_SAVING(false))
	}
}

function* mainSaga() {
	yield takeEvery(settlementActions.TRIGGER_INIT_SETTLEMENT_PAGE, init)
	yield takeLatest(settlementActions.YEAR, fetchSummary)
	yield takeLatest(settlementActions.MONTH, fetchRows)
	yield takeLatest(settlementActions.WEEK, fetchRows)
	yield takeLatest(settlementActions.UNIT, fetchRows)
	yield takeLatest(settlementActions.CURRENCY, fetchRows)
	yield takeEvery(settlementActions.TRIGGER_UPDATE, updateOne)
	yield takeEvery(settlementActions.TRIGGER_BULK, bulk)
	yield takeEvery(settlementActions.TRIGGER_MARK_PAID, markPaid)
}

export default function* rootSaga() {
	yield all([fork(mainSaga)])
}
